import axios from 'axios';
import type { LoginRequest, TwoFactorLoginRequest, LoginResponse, RefreshResponse, TotpSetupResponse } from '../types/AuthTypes';

const getApiUrl = () => {
  if (window.location.hostname === 'fs.stevegala.com') {
    return 'https://fs.stevegala.com';
  }
  return 'http://localhost:54321';
};

// Separate axios instance for auth (no interceptors to avoid circular refresh loops)
const authClient = axios.create({
  baseURL: getApiUrl(),
  timeout: 10000,
});

const TOKEN_KEY = 'fs_access_token';
const REFRESH_KEY = 'fs_refresh_token';

export const authService = {
  async login(request: LoginRequest): Promise<LoginResponse> {
    const response = await authClient.post('/api/auth/login', request);
    return response.data;
  },

  async loginWith2FA(request: TwoFactorLoginRequest): Promise<LoginResponse> {
    const response = await authClient.post('/api/auth/login/2fa', request);
    return response.data;
  },

  async setup2FA(setupToken: string): Promise<TotpSetupResponse> {
    const response = await authClient.post('/api/auth/2fa/setup', { setupToken });
    return response.data;
  },

  async confirm2FA(setupToken: string, totpCode: string): Promise<LoginResponse> {
    const response = await authClient.post('/api/auth/2fa/confirm', { setupToken, totpCode });
    return response.data;
  },

  async refresh(): Promise<RefreshResponse> {
    const response = await authClient.post('/api/auth/refresh', {
      refreshToken: this.getRefreshToken(),
    });
    return response.data;
  },

  async logout(): Promise<void> {
    try {
      await authClient.post(
        '/api/auth/logout',
        { refreshToken: this.getRefreshToken() },
        { headers: { Authorization: `Bearer ${this.getAccessToken()}` } }
      );
    } finally {
      this.clearTokens();
    }
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await authClient.post(
      '/api/auth/change-password',
      { currentPassword, newPassword },
      { headers: { Authorization: `Bearer ${this.getAccessToken()}` } }
    );
  },

  // Token storage
  saveTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_KEY, refreshToken);
  },

  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },

  clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },

  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  },

  parseToken(token: string): { sub: string; unique_name: string; role: string; perm?: string | string[] } | null {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch {
      return null;
    }
  },

  // Normalizes the `perm` claim — JSON serializer emits a string for a single claim
  // or an array for multiple. Always returns a flat string[].
  extractPermissions(payload: { perm?: string | string[] } | null): string[] {
    if (!payload?.perm) return [];
    if (Array.isArray(payload.perm)) return payload.perm.filter((p): p is string => typeof p === 'string');
    if (typeof payload.perm === 'string') return [payload.perm];
    return [];
  },
};
